import { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPen, faPlus, faXmark } from '@fortawesome/pro-solid-svg-icons';
import { Button } from '@design-system/components/button/button';
import { Col, Row } from '@design-system/components/grid/grid';
import { Toggle } from '@client/components/common/inputs/Toggle';
import { Typography } from '@design-system/components/typography/typography';
import { MediaEditor } from '../../../workout/components/MediaEditor';
import { PoseGender, PoseGuide } from '../../model/Challenges.types';
import {
  MAX_POSES,
  POSE_PHOTO_HEIGHT,
  POSE_PHOTO_WIDTH,
} from '../../pose-guide.constants';
import { PoseModal } from './PoseModal';
import { DeletePoseModal } from './DeletePoseModal';
import * as s from './PoseGuideList.css';

type ModalState =
  | { type: 'none' }
  | { type: 'add' }
  | { type: 'edit'; index: number }
  | { type: 'delete'; index: number };

type PoseGuideListProps = {
  poses: PoseGuide[];
  poseGender: PoseGender;
  uploadImage: (file: File) => Promise<string>;
  onSelectGender: (gender: PoseGender) => void;
  onReset: () => void;
  onAdd: (pose: PoseGuide) => void;
  onUpdate: (index: number, pose: PoseGuide) => void;
  onRemove: (index: number) => void;
};

const emptyPose: PoseGuide = { name: '', imageUrl: '' };

export const PoseGuideList = (p: PoseGuideListProps) => {
  const [modal, setModal] = useState<ModalState>({ type: 'none' });
  const canAdd = p.poses.length < MAX_POSES;
  const close = () => setModal({ type: 'none' });

  return (
    <Col gap="xl">
      <Row gap="l" alignCenter>
        <Row gap="m" alignCenter>
          <Typography variant="body">Female</Typography>
          <div className={s.genderToggle}>
            <Toggle
              value={p.poseGender === 'male'}
              onChange={(v) => p.onSelectGender(v ? 'male' : 'female')}
            />
          </div>
          <Typography variant="body">Male</Typography>
        </Row>
        <Button
          color="primaryTonal"
          variant="outlined"
          size="medium"
          onClick={p.onReset}
        >
          {p.poses.length === 0 ? 'Apply defaults' : 'Reset to defaults'}
        </Button>
      </Row>

      <div className={s.list}>
        {p.poses.map((pose, index) => (
          <Col key={index} className={s.card} gap="m">
            <Row alignCenter style={{ justifyContent: 'space-between' }}>
              <Typography variant="body" fontWeight="medium">
                {pose.name}
              </Typography>
              <Row gap="m" alignCenter>
                <FontAwesomeIcon
                  icon={faPen}
                  className={s.icon}
                  data-testid="edit-pose"
                  onClick={() => setModal({ type: 'edit', index })}
                />
                <FontAwesomeIcon
                  icon={faXmark}
                  className={s.deleteIcon}
                  data-testid="delete-pose"
                  onClick={() => setModal({ type: 'delete', index })}
                />
              </Row>
            </Row>
            <MediaEditor
              readOnly
              format="image"
              accept="image/*"
              src={pose.imageUrl || null}
              loading={false}
              disabled={false}
              width={POSE_PHOTO_WIDTH}
              height={POSE_PHOTO_HEIGHT}
            />
          </Col>
        ))}

        {canAdd && (
          <div className={s.addCard} onClick={() => setModal({ type: 'add' })}>
            <FontAwesomeIcon icon={faPlus} />
            <Typography variant="body" fontWeight="medium">
              Add Pose
            </Typography>
          </div>
        )}
      </div>

      {(modal.type === 'add' || modal.type === 'edit') && (
        <PoseModal
          key={modal.type === 'edit' ? `edit-${modal.index}` : 'add'}
          mode={modal.type}
          initial={modal.type === 'edit' ? p.poses[modal.index] : emptyPose}
          uploadImage={p.uploadImage}
          onSave={(pose) => {
            if (modal.type === 'edit') p.onUpdate(modal.index, pose);
            else p.onAdd(pose);
            close();
          }}
          onClose={close}
        />
      )}

      {modal.type === 'delete' && (
        <DeletePoseModal
          poseName={p.poses[modal.index]?.name || ''}
          onConfirm={() => {
            p.onRemove(modal.index);
            close();
          }}
          onClose={close}
        />
      )}
    </Col>
  );
};
